import { Page } from "@towersystems/roam-common-native";
import { Camera, CameraProps, CameraType } from "expo-camera";
import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { View } from "react-native-ui-lib";
import { PermissionsManager } from "../../utilities/permissions";
import { PermissionToken } from "../../utilities/permissions/constants";
import { PermissionGuard } from "../PermissionGuard";
import { CameraHeader, CameraHeaderProps, OpaqueOverlay } from "./components";
import { CameraProvider } from "./components/CameraProvider";
import { IconButton } from "./components/IconButton";
import { TextButton } from "./components/TextButton";
import { useStreamProvider } from "../../utilities/barcode-scanner/hooks";
import { useCameraContext } from "../../utilities/camera";
import {
  usePrepareRatio,
  ratioStringToNumber,
} from "../../utilities/camera/utils";
import { useFocusEffect } from "@react-navigation/core";

export interface CameraScreenProps {
  cameraProps?: Omit<CameraProps, "onBarCodeScanned">;
  headerProps?: CameraHeaderProps;
  previewElement?: JSX.Element;
  overlayElement?: JSX.Element;
  fullScreen?: boolean;
}

export const CameraViewFinder = ({
  cameraProps = {},
  headerProps,
  previewElement,
  overlayElement,
}: CameraScreenProps) => {
  const context = useCameraContext();

  const { cleanupStream } = useStreamProvider();

  const [isBlocked, setIsBlocked] = useState<boolean>(true);

  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);

  const { cameraRef } = context;

  const { ...spreadableCameraProps } = cameraProps;

  function handleCameraReady() {
    // TODO: hack since onCameraReady is called before camera preview is ready...
    setTimeout(() => setIsCameraReady(true), 150);
  }

  const overlayMarkup = (
    <OpaqueOverlay showChildren={isCameraReady}>{overlayElement}</OpaqueOverlay>
  );

  useEffect(() => {
    PermissionsManager.getManagedPermission(PermissionToken.Camera)
      .getPermissionResponse()
      .then((permissionResponse) => {
        setIsBlocked(!permissionResponse.granted);
      })
      .catch(() => {
        console.log("error requesting camera permissions");
      });

    const cleanup = PermissionsManager.getManagedPermission(
      PermissionToken.Camera
    ).addOnChangeListener((permitted) => {
      if (permitted) {
        setIsBlocked(false);
      }
    });
    return () => cleanup();
  }, []);

  useLayoutEffect(() => {
    cleanupStream();
  }, []);

  const { imagePadding, ratio } = usePrepareRatio();

  // only render Camera if permissions are granted
  // this prevents automatic permission request popups in favour of our own modal
  return (
    <Page scrollable={false} padding={0} fullWidth>
      <PermissionGuard
        allowScreen={false}
        permissions={[PermissionToken.Camera]}
      >
        <CameraHeader {...headerProps} />
        {previewElement && <View style={bringToFront}>{previewElement}</View>}
        {isBlocked ? (
          overlayMarkup
        ) : (
          <View
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
            }}
          >
            <Camera
              onCameraReady={handleCameraReady}
              type={CameraType.back}
              ref={cameraRef}
              {...spreadableCameraProps}
              ratio={ratio}
              pictureSize={ratio}
              style={[
                {
                  flex: 1,
                  aspectRatio: ratioStringToNumber(ratio),
                  width: "100%",
                  position: "absolute",
                },
              ]}
            ></Camera>
            <View
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                left: 0,
                bottom: 0,
              }}
            >
              {overlayMarkup}
            </View>
          </View>
        )}
      </PermissionGuard>
    </Page>
  );
};

CameraViewFinder.Header = CameraHeader;
CameraViewFinder.Provider = CameraProvider;
CameraViewFinder.IconButton = IconButton;
CameraViewFinder.TextButton = TextButton;

const bringToFront = {
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  top: 0,
  zIndex: 1,
} as const;
